<template>
    <div class="module">
        <header class="module__header--with-disclaimer">
            <h1>Search Index Insights</h1>
        </header>

        <section class="module__body">
            <DataTable
                :value="objectCounts"
                class="objects-table"
                :loading="isLoading"
            >
                <template #header>
                    <div class="header">
                        <h1>Indexed Objects</h1>
                        <div>
                            <h2>
                                Total: <strong data-test-id="total-objects">{{ total }}</strong>
                            </h2>
                            <PrimeButton
                                icon="pi pi-refresh"
                                rounded
                                raised
                                data-test-id="refresh-button"
                                @click="loadStats"
                            />
                        </div>
                    </div>
                </template>
                <Column
                    field="index"
                    header="Index"
                >
                    <template #body="slotProps">
                        <div :data-test-id="`row-index-${slotProps.data.name}`">
                            {{ slotProps.data.index }}
                        </div>
                    </template>
                </Column>
                <Column
                    field="name"
                    header="Object Type"
                >
                    <template #body="slotProps">
                        <div :data-test-id="`row-object-${slotProps.data.name}`">
                            {{ slotProps.data.name }}
                        </div>
                    </template>
                </Column>
                <Column
                    field="count"
                    header="Object Count"
                >
                    <template #body="slotProps">
                        <div :data-test-id="`row-count-${slotProps.data.name}`">
                            {{ slotProps.data.count }}
                        </div>
                    </template>
                </Column>
                <Column
                    field="degradedCount"
                    header="Degraded"
                >
                    <template #body="slotProps">
                        <div :data-test-id="`row-degraded-${slotProps.data.name}`">
                            {{ slotProps.data.degradedCount }}
                            <div
                                v-if="slotProps.data.degradedFields.length"
                                class="degraded-fields"
                                :data-test-id="`row-degraded-fields-${slotProps.data.name}`"
                            >
                                {{ slotProps.data.degradedFields.join(', ') }}
                            </div>
                        </div>
                    </template>
                </Column>
                <Column header="Actions">
                    <template #body="slotProps">
                        <PrimeButton
                            v-if="can('delete search indexes')"
                            icon="pi pi-trash"
                            :data-test-id="`flush-${slotProps.data.name}-button`"
                            label="Flush"
                            severity="danger"
                            raised
                            @click.prevent="flush(slotProps.data.name)"
                        />&nbsp;
                        <PrimeButton
                            v-if="can('update search indexes')"
                            icon="pi pi-refresh"
                            :data-test-id="`reindex-${slotProps.data.name}-button`"
                            label="Reindex"
                            raised
                            @click.prevent="reindex(slotProps.data.name)"
                        />
                    </template>
                </Column>
                <template #footer>
                    <div class="footer">
                        <PrimeButton
                            v-if="can('delete search indexes')"
                            icon="pi pi-trash"
                            raised
                            label="Flush All"
                            everity="danger"
                            data-test-id="flush-all-button"
                            @click.prevent="flushAll()"
                        />&nbsp;
                        <PrimeButton
                            v-if="can('update search indexes')"
                            icon="pi pi-refresh"
                            data-test-id="reindex-all-button"
                            label="Reindex All"
                            raised
                            @click.prevent="reindexAll()"
                        />
                    </div>
                </template>
            </DataTable>
        </section>
    </div>
</template>

<script>
import _ from 'lodash'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import PrimeButton from 'primevue/button'
import canToast from '@/mixins/canToast'
import canConfirm from '@/mixins/canConfirm'
import Permissions from '@/mixins/permissions'
import 'primeicons/primeicons.css'

export default {
    components: { DataTable, Column, PrimeButton },
    mixins: [canToast, canConfirm, Permissions],

    data() {
        return {
            total: null,
            objectCounts: [],
            isLoading: true,
        }
    },

    mounted() {
        this.loadStats()
    },

    methods: {
        loadStats() {
            this.isLoading = true
            return this.$http
                .get(`search/objects`)
                .then(({ data }) => {
                    this.total = data.total
                    this.objectCounts = []
                    _.forEach(data.counts, (value, index) => {
                        // Documents OpenSearch indexed with a field skipped.
                        const degraded = _.get(data, ['degraded', index], {})

                        this.objectCounts.push({
                            name: index,
                            count: value,
                            index: data.index,
                            degradedCount: degraded.count || 0,
                            degradedFields: _.keys(degraded.fields || {}),
                        })
                    })
                })
                .catch((error) => {
                    this.requestError('There was a problem fetching index information.')
                })
                .finally(() => {
                    this.isLoading = false
                })
        },

        async flush(type) {
            const isConfirmed = await this.confirm({
                message: `Are you sure you want to flush all indexed data for objects of type ${type.toUpperCase()}?`,
                acceptLabel: 'Flush',
                danger: true,
            })
            if (isConfirmed) {
                this.isLoading = true
                return this.$http
                    .delete(`search/objects?object_type=${type}`)
                    .then(({ data }) => {
                        return this.loadStats()
                    })
                    .catch((error) => {
                        this.requestError('There was a problem flushing this object type.')
                    })
                    .finally(() => {
                        this.isLoading = false
                    })
            }

            return false
        },

        async flushAll() {
            const isConfirmed = await this.confirm({
                message: 'Are you sure you want to flush ALL indexed data?',
                acceptLabel: 'Flush',
                danger: true,
            })
            if (isConfirmed) {
                this.isLoading = true
                return this.$http
                    .delete(`search/objects`)
                    .then(({ data }) => {
                        return this.loadStats()
                    })
                    .catch((error) => {
                        this.requestError('There was a problem flushing data.')
                    })
                    .finally(() => {
                        this.isLoading = false
                    })
            }

            return false
        },

        async reindex(type) {
            const isConfirmed = await this.confirm({
                message: `Are you sure you want to fully reindex all objects of type ${type.toUpperCase()}?`,
                acceptLabel: 'Reindex',
            })
            if (isConfirmed) {
                this.isLoading = true
                this.toast(
                    `Reindexing ${type.toUpperCase()} objects has started. Please wait a bit and refresh.`,
                    'info',
                    10000
                )
                return this.$http
                    .post(`search/objects`, { object_type: type })
                    .then(({ data }) => {
                        return this.loadStats()
                    })
                    .catch((error) => {
                        this.requestError('There was a problem reindexing this object type.')
                    })
                    .finally(() => {
                        this.isLoading = false
                    })
            }

            return false
        },

        async reindexAll() {
            const isConfirmed = await this.confirm({
                message: 'Are you sure you want to fully reindex ALL searchable objects?',
                acceptLabel: 'Reindex',
            })
            if (isConfirmed) {
                this.isLoading = true
                this.toast('Reindexing ALL objects has started. Please wait a bit and refresh.', 'info', 10000)
                return this.$http
                    .post(`search/objects`)
                    .then(({ data }) => {
                        return this.loadStats()
                    })
                    .catch((error) => {
                        this.requestError('There was a problem reindexing.')
                    })
                    .finally(() => {
                        this.isLoading = false
                    })
            }

            return false
        },

        requestError(message) {
            this.toast(message, 'error', 6000)
        },
    },
}
</script>

<style scoped lang="scss">
.objects-table {
    font-family: 'Avenir', sans-serif;
    .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        strong {
            font-family: 'Avenir Heavy', sans-serif;
            font-weight: bold;
        }
        h1 {
            font-size: 24px;
        }
        h2 {
            margin-right: 10px;
            color: gray;
        }
        div {
            display: flex;
            align-items: center;
        }
    }
    .degraded-fields {
        font-size: 12px;
        color: gray;
    }
}
</style>
